import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import videoSrc from "../../assets/videos/documentary.mov";
import overlayImg from "../../assets/images/hero-image-12.jpg";

export default function MaskedVideoHero() {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);
  const soundEnabled = useRef(false);
  const [device, setDevice] = useState("desktop");

  /* ================= DEVICE ================= */
  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 768) setDevice("mobile");
      else if (window.innerWidth < 1024) setDevice("tablet");
      else setDevice("desktop");
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  /* ================= ENABLE SOUND ON FIRST INTERACTION ================= */
  useEffect(() => {
    const enableSound = () => {
      if (soundEnabled.current) return;
      const video = videoRef.current;
      if (!video) return;

      soundEnabled.current = true;
      video.muted = false;
      video.volume = 1;

      video.play().catch(() => {});
    };

    window.addEventListener("click", enableSound, { once: true });
    window.addEventListener("touchstart", enableSound, { once: true });
    window.addEventListener("keydown", enableSound, { once: true });

    return () => {
      window.removeEventListener("click", enableSound);
      window.removeEventListener("touchstart", enableSound);
      window.removeEventListener("keydown", enableSound);
    };
  }, []);

  /* ================= SCROLL ================= */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const scroll = useSpring(scrollYProgress, {
    stiffness: 60,
    damping: 28,
    mass: 1.1,
  });

  /* ================= UN-REVEAL TIMING ================= */
  const hideEarly = useTransform(scroll, [0.15, 0.30], [1, 0]);
  const hideMid   = useTransform(scroll, [0.18, 0.33], [1, 0]);
  const hideLate  = useTransform(scroll, [0.22, 0.38], [1, 0]);

  const moveEarly = useTransform(scroll, [0.15, 0.30], [0, -20]);
  const moveMid   = useTransform(scroll, [0.18, 0.33], [0, -20]);
  const moveLate  = useTransform(scroll, [0.22, 0.38], [0, -20]);

  const overlayOpacity = useTransform(scroll, [0, 0.5], [0.85, 0.99]);

  /* ================= SHARP RECT PATH ================= */
  const pathD = useTransform(scroll, (v) => {
    const t = Math.min(v / 0.35, 1);

    const config = {
      desktop: { x: 6 - t * 6, y: 15 - t * 1, w: 42 + t * 58, h: 70 + t * 2 },
      tablet:  { x: 4 - t * 4, y: 20 - t * 1, w: 55 + t * 45, h: 59 + t * 2 },
      mobile:  { x: 5 - t * 5, y: 21 - t * 2, w: 90 + t * 10, h: 59 + t * 4 },
    }[device];

    const { x, y, w, h } = config;

    return `
      M ${x} ${y}
      H ${x + w}
      V ${y + h}
      H ${x}
      Z
    `;
  });

  /* ================= RENDER ================= */
  return (
    <section ref={sectionRef} className="relative h-[300vh] bg-black">
      <div className="sticky top-0 h-screen overflow-hidden relative isolate">

        {/* VIDEO */}
        <video
          ref={videoRef}
          src={videoSrc}
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* MASK */}
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 z-20 w-full h-full overflow-hidden pointer-events-none"
        >
          <defs>
            <mask id="bb-mask">
              <rect x="0" y="0" width="100" height="100" fill="white" />
              <motion.path d={pathD} fill="black" />
            </mask>
          </defs>

          <image
            href={overlayImg}
            x="0"
            y="0"
            width="100"
            height="100"
            preserveAspectRatio="xMidYMid slice"
            mask="url(#bb-mask)"
          />

          <motion.rect
            x="0"
            y="0"
            width="100"
            height="100"
            fill="rgb(5 17 24)"
            opacity={overlayOpacity}
            mask="url(#bb-mask)"
          />
        </svg>

        {/* ================= TEXT CONTENT ================= */}
        <div className="relative z-30 h-full ml-20 pointer-events-none">
          <div className="max-w-7xl mx-auto h-full px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full items-center">
              <div />
              <div className="space-y-10 text-white">
                <motion.h1
                  style={{ opacity: hideEarly, y: moveEarly }}
                  className="text-5xl md:text-6xl xl:text-6xl font-bold leading-tight tracking-tight"
                >
                  Glimpses from<br />
                  <span className="text-gray-400">
                    the edge of<br />elevation
                  </span>
                </motion.h1>

                <motion.p
                  style={{ opacity: hideLate, y: moveLate }}
                  className="text-xl text-gray-300 max-w-xl"
                >
                  Documentary glimpses from remote expeditions.<br />
                  A visual archive of journeys where conditions are unforgiving
                  and moments are real.
                </motion.p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
